"use client";

import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Sparkles,
  Trophy,
  Users,
  Star,
  LineChart,
  Phone,
  MessageCircle,
  ArrowRight,
  GraduationCap,
  HeartHandshake,
  HelpCircle,
  Wrench,
  MapPinned,
  CalendarClock,
  Share2,
  Tv,
  ExternalLink,
  BookOpen,
  BrainCircuit,
  BarChart3,
  Zap,
  Layers,
} from "lucide-react";
import { OrbitingIcons } from "@/components/orbiting-icons";
import FanOutDeck from "@/components/fan-out-deck";
import Link from "next/link";
import Image from "next/image";

import { useRef, useEffect, useState, type FormEvent } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import {
  SITE_PHONE_E164,
  SITE_WHATSAPP_URL_DEFAULT,
  GOOGLE_MAPS_DIRECTIONS_URL,
  GOOGLE_REVIEWS_SEARCH_URL,
  INSTAGRAM_URL,
  YOUTUBE_URL,
  SITE_TOOL_ENTRIES,
  whatsappUrl,
} from "@/lib/site-config";
import { listCourseRecords } from "@/lib/courses-catalog";
import { cn } from "@/lib/utils";
import { fetchPublicSuccessStories, type PublicSuccessStory } from "@/lib/catalog-api";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const EXPLORE_PAGES = [
  {
    href: "/about",
    title: "About Us",
    description: "Mission, teaching philosophy, and how we train analysts.",
    icon: GraduationCap,
  },
  {
    href: "/courses",
    title: "All Courses",
    description: "Browse brochure-aligned programs with INR fees on every listing.",
    icon: BookOpen,
  },
  {
    href: "/upcoming-batch",
    title: "Upcoming Batch",
    description: "Latest intake timing — confirm seats directly with our counselors.",
    icon: CalendarClock,
  },
  {
    href: "/why-us",
    title: "Why Us?",
    description: "Trainer credibility, outcomes focus, and learner-friendly delivery.",
    icon: HeartHandshake,
  },
  {
    href: "/faq",
    title: "FAQ",
    description: "Fees, EMI batches, and common enrollment questions answered.",
    icon: HelpCircle,
  },
  {
    href: "/tools",
    title: "Tools",
    description: "Python, ML, BI, SAS, SQL, Excel & more — aligned with Mont2 brochure.",
    icon: Wrench,
  },
  {
    href: "/reviews",
    title: "Reviews & Maps",
    description: "Google ratings, social presence, and directions to the centre.",
    icon: MapPinned,
  },
  {
    href: "/contact",
    title: "Contact Us",
    description: "Phone, email, enquiry desk, and classroom address.",
    icon: Phone,
  },
  {
    href: "/whatsapp",
    title: "WhatsApp Desk",
    description: "Jump straight into chat for fees, EMI, and batch updates.",
    icon: MessageCircle,
  },
];

const BENTO_HIGHLIGHTS = [
  {
    href: "/courses/360-data-science",
    title: "360 Data Science",
    subtitle: "Full-stack analytics to placement-ready depth",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1400",
  },
  {
    href: "/courses/data-analysts",
    title: "Data Analyst",
    subtitle: "Models, dashboards, and SQL in one track",
    image:
      "https://images.unsplash.com/photo-1555949963-aa79dcee981c?auto=format&fit=crop&q=80&w=1200",
  },
  {
    href: "/upcoming-batch",
    title: "Next batch window",
    subtitle: "INR fees & EMI on the batch page",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1200",
  },
];

const STORY_BLOCKS = [
  {
    href: "/about",
    eyebrow: "Why Mont2",
    title: "Built like a product team, taught like a classroom",
    body: "You work on structured assignments, real datasets, and presentation-ready visuals — the same rhythm analytics teams use in industry.",
    image:
      "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&q=80&w=1200",
    imageLeft: true,
  },
  {
    href: "/tools",
    eyebrow: "Tooling",
    title: "From Python & ML to Tableau, Power BI, SAS & SQL",
    body: "Stack coverage follows the Mont2 brochure so your portfolio lines up with what hiring managers expect from analysts and scientists.",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200",
    imageLeft: false,
  },
];

// Hardcoded fallback — shown instantly while API loads or if API fails
const FALLBACK_STORIES: PublicSuccessStory[] = [
  { id: 's1', name: "Pulkit Rohilla",   role: "Google Review", image: "https://ui-avatars.com/api/?name=Pulkit+Rohilla&background=8b5cf6&color=fff",   quote: "Mont2 Academy is one of the best places for learning Data Science. The institute provides a well-structured curriculum with practical, industry-relevant training. A special appreciation for Harish Sir, who is an exceptional trainer." },
  { id: 's2', name: "Ria Alixe George", role: "Google Review", image: "https://lh3.googleusercontent.com/a-/ALV-UjVUNyrRjxrg-tXBRO6Om6TNrO4Ut1dUkLSv_FjpszcgjAZuh6PP=w90-h90-p-rp-mo-br100", quote: "The best Data Science institute in South Delhi, Harish sir is hands down the most helpful and dedicated teacher I have seen. Even if you are from a non tech background everything is taught to you in detail so that you can understand." },
  { id: 's3', name: "Shilpi Gochhwal",  role: "Google Review", image: "https://ui-avatars.com/api/?name=Shilpi+Gochhwal&background=f43f5e&color=fff",  quote: "I am pursuing data science course in Mont2 academy and had a great experience so far. The curriculum is well-structured and covers everything from basic statistics to advanced machine learning. The trainer is highly knowledgeable." },
  { id: 's4', name: "Punit Chahal",     role: "Google Review", image: "https://ui-avatars.com/api/?name=Punit+Chahal&background=0ea5e9&color=fff",     quote: "A top institute for learning data analytics and data science tools under the guidance of experienced IT professionals who emphasize a hands-on, real-world approach. The curriculum is designed to simulate actual industry challenges." },
  { id: 's5', name: "Aarti Pundir",     role: "Google Review", image: "https://ui-avatars.com/api/?name=Aarti+Pundir&background=10b981&color=fff",     quote: "Mont2 academy is one of the best places for learning data science. I really believe that if you want to pursue a career in data science or any other data related subject, you should join it." },
  { id: 's6', name: "Vandana Raghav",   role: "Google Review", image: "https://ui-avatars.com/api/?name=Vandana+Raghav&background=f59e0b&color=fff",   quote: "I had a great experience with MONT2 Academy. The course was job oriented, and the mentors provided excellent guidance. With their support, I gained hands-on skills and landed a job in one of the Big 3 companies." },
  { id: 's7', name: "Arpit Gupta",      role: "Google Review", image: "https://ui-avatars.com/api/?name=Arpit+Gupta&background=6366f1&color=fff",      quote: "Great mentor! Covers everything in detail and ensures that each and every student understands the concepts. Extensive focus on practice." },
  { id: 's8', name: "Devansh Attri",    role: "Google Review", image: "https://ui-avatars.com/api/?name=Devansh+Attri&background=d946ef&color=fff",    quote: "I did my Data analyst course from Harish Sir, and he's an incredible teacher. Even though I had no technical background, he managed to teach me everything from scratch." },
  { id: 's9', name: "Anand Acharya",    role: "Google Review", image: "https://lh3.googleusercontent.com/a-/ALV-UjV8B4z275cMbaGl_XHY4NyNuOunY8xRUhG1FXZ5-mAMVIvXYLnNSg=w90-h90-p-rp-mo-ba4-br100", quote: "Harish Sir is a great teacher — always supportive and generous with his insights. He not only shares deep knowledge but also takes the time to explain concepts clearly during the course. Highly recommended!" },
];

export default function Home() {
  const containerRef = useRef<HTMLDivElement>(null);
  const blobRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stories, setStories] = useState<PublicSuccessStory[]>(FALLBACK_STORIES);

  // Fetch live stories from API; fall back silently to hardcoded data on error
  useEffect(() => {
    fetchPublicSuccessStories().then((data) => {
      if (data && data.length > 0) setStories(data);
    }).catch(() => { /* keep fallback */ });
  }, []);

  const marqueeStories = [...stories, ...stories];

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
    if (prefersReducedMotion || isCoarsePointer) return;

    const handlePointerMove = (e: PointerEvent) => {
      if (blobRef.current) {
        blobRef.current.animate(
          {
            left: `${e.clientX}px`,
            top: `${e.clientY}px`,
          },
          { duration: 3000, fill: "forwards" },
        );
      }
    };
    window.addEventListener("pointermove", handlePointerMove);
    return () => window.removeEventListener("pointermove", handlePointerMove);
  }, []);

  useGSAP(
    () => {
      gsap.from(".hero-content > *", {
        y: 50,
        opacity: 0,
        duration: 1,
        stagger: 0.1,
        ease: "power3.out",
      });

      const sections = gsap.utils.toArray<HTMLElement>(".gsap-section");

      sections.forEach((section) => {
        gsap.from(section.querySelectorAll(".gsap-fade-up"), {
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
          y: 40,
          opacity: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power2.out",
        });
      });

      gsap.from(".stat-card", {
        scrollTrigger: {
          trigger: ".stats-section",
          start: "top 85%",
        },
        scale: 0.8,
        opacity: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "back.out(1.7)",
      });

      const statNumbers = gsap.utils.toArray<HTMLElement>(".stat-number");
      statNumbers.forEach((el) => {
        const targetAttr = el.getAttribute("data-target");
        if (!targetAttr) return;
        const target = parseFloat(targetAttr);
        if (isNaN(target) || target === 0) return;

        const suffix = el.getAttribute("data-suffix") || "";
        const decimals = parseInt(el.getAttribute("data-decimals") || "0");

        const obj = { val: 0 };

        gsap.to(obj, {
          scrollTrigger: {
            trigger: ".stats-section",
            start: "top 85%",
          },
          val: target,
          duration: 2.5,
          ease: "power3.out",
          onUpdate: () => {
            el.innerText =
              obj.val.toLocaleString("en-US", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
              }) + suffix;
          },
        });
      });
    },
    { scope: containerRef },
  );

  async function handleHeroEnquiry(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const payload = {
      name: String(fd.get("name") ?? "").trim(),
      phone: String(fd.get("phone") ?? "").trim(),
      email: String(fd.get("email") ?? "").trim(),
      course: String(fd.get("course") ?? "").trim(),
      message: String(fd.get("message") ?? "").trim(),
    };

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert("Enquiry sent successfully! We will get back to you soon.");
        form.reset();
      } else {
        alert("Failed to send enquiry. Please try again later.");
      }
    } catch (error) {
      console.error(error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div ref={containerRef} className="site-page min-h-screen flex flex-col relative">
      <div
        ref={blobRef}
        className="fixed top-1/2 left-1/2 w-[120px] h-[120px] sm:w-[200px] sm:h-[200px] bg-[#F97316]/60 rounded-full blur-[80px] pointer-events-none z-40 -translate-x-1/2 -translate-y-1/2 dark:mix-blend-screen mix-blend-multiply hidden md:block"
      />

      <Navbar />

      <main className="relative z-10 pt-20">
        {/* 1. Hero */}
        <section className="relative min-h-[100dvh] flex items-center justify-center pt-16 pb-16 sm:pt-20 sm:pb-24 overflow-hidden bg-background">
          <div className="absolute inset-0 bg-grid opacity-50 dark:opacity-20 z-0 mask-image-gradient" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-mont-violet/20 dark:from-mont-violet/30 via-background to-background z-0 opacity-80" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] sm:w-[400px] sm:h-[400px] md:w-[600px] md:h-[600px] bg-mont-orange/15 dark:bg-mont-orange/40 rounded-full blur-[100px] sm:blur-[140px] pointer-events-none z-0" />

          <div className="site-container relative z-10">
            <div className="relative flex min-h-[420px] flex-col items-center justify-center pt-8 sm:min-h-[480px] sm:pt-12 md:min-h-0 md:pt-20">
              <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none opacity-60 sm:opacity-50 md:opacity-55 lg:opacity-70 xl:opacity-100">
                <OrbitingIcons />
              </div>

              <div className="hero-content relative z-10 flex flex-col gap-5 sm:gap-8 max-w-4xl mx-auto text-center items-center px-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-emerald-500 text-xs sm:text-sm font-semibold tracking-wide shadow-sm backdrop-blur-sm animate-fade-in">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  ISO 9001:2015 Certified Academy
                </div>
                <h1 className="flex flex-col items-center gap-1 sm:gap-2 md:gap-4 text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tight text-foreground drop-shadow-2xl font-heading font-bold">
                  <span className="leading-none">Become a</span>
                  <span className="leading-none">
                    <span className="text-gradient font-semibold">Data Wizard</span>{" "}
                  </span>
                </h1>

                <div className="text-center space-y-2 sm:space-y-3 mt-2 sm:mt-4">
                  <p className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground">
                    Learn. Build. Analyze. Get Hired.
                  </p>
                  <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed font-medium px-2">
                    Hands-on Data Analytics & Data Science training with live projects and expert mentorship.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3 sm:gap-4 w-full max-w-md sm:max-w-none sm:w-auto px-4 sm:px-0">
                  <Link href="/upcoming-batch" className="w-full sm:w-auto">
                    <Button
                      size="lg"
                      className="h-12 sm:h-14 w-full sm:w-auto px-6 sm:px-8 text-base sm:text-lg font-bold bg-foreground text-background hover:bg-foreground/90 border-0 transition-all shadow-[0_0_40px_rgba(139,92,246,0.3)]"
                    >
                      Start learning
                    </Button>
                  </Link>
                  <Link href="/courses" className="w-full sm:w-auto">
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-12 sm:h-14 w-full sm:w-auto px-6 sm:px-8 text-base sm:text-lg font-bold border-border text-foreground hover:bg-foreground/5 transition-all bg-background/50 backdrop-blur-sm"
                    >
                      Explore Curriculum
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Stats Section */}
        <section className="py-10 sm:py-12 border-y border-border bg-card/30 stats-section relative z-10">
          <div className="site-container grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 md:gap-8">
            {[
              {
                icon: <Users className="text-mont-violet" />,
                value: "Classroom",
                rawValue: 0,
                suffix: "",
                decimals: 0,
                label: "& Live Sessions",
              },
              {
                icon: <Trophy className="text-mont-orange" />,
                value: "100%",
                rawValue: 100,
                suffix: "%",
                decimals: 0,
                label: "Job Assistance",
              },
              {
                icon: <Star className="text-yellow-400" />,
                value: "5 / 5",
                rawValue: 5.0,
                suffix: " / 5",
                decimals: 0,
                label: "Google Review: 5-Star Rating",
              },
              {
                icon: <LineChart className="text-mont-violet" />,
                value: "47.8",
                rawValue: 47.8,
                suffix: " LPA",
                decimals: 1,
                label: "Highest Salary",
              },
            ].map((stat, i) => (
              <div
                key={i}
                className={cn(
                  "stat-card flex flex-col items-center text-center px-2 sm:px-4",
                  i % 2 === 0 && "max-md:border-r max-md:border-border/50",
                  i < 2 && "max-md:border-b max-md:border-border/50 max-md:pb-6",
                  i % 4 !== 3 && "md:border-r md:border-border/60",
                )}
              >
                <div className="mb-2 sm:mb-3 p-2 sm:p-3 bg-white/5 rounded-xl">{stat.icon}</div>
                <h3
                  className="text-2xl sm:text-3xl font-extrabold font-heading text-foreground mb-1 stat-number"
                  data-target={stat.rawValue}
                  data-suffix={stat.suffix}
                  data-decimals={stat.decimals}
                >
                  {stat.value}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground font-medium leading-snug">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 3. Logos Marquee */}
        <section className="py-12 border-b border-border bg-background overflow-hidden relative z-10">
          <div className="container mx-auto px-6 mb-8 text-center">
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-widest">Our students are placed at</p>
          </div>
          <div className="relative flex max-w-full overflow-hidden bg-background">
            <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-24 bg-gradient-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-24 bg-gradient-to-l from-background to-transparent" />
            <div className="flex w-max animate-marquee items-center gap-12 px-4 hover:[animation-play-state:paused]">
              {[...Array(3)].map((_, idx) => (
                <div key={idx} className="flex items-center gap-12">
                  {[
                    { name: "Barclays", url: "/compnay_logo/barclays.jpg" },
                    { name: "HSBC", url: "/compnay_logo/hsbc.jpg" },
                    { name: "PwC", url: "/compnay_logo/pwc.jpg" },
                    { name: "Bank of America", url: "/compnay_logo/Bankofamerica.jpg" },
                    { name: "JPMorgan", url: "/compnay_logo/jpmorgan.jpg" },
                    { name: "Google", url: "/compnay_logo/google.jpg" },
                    { name: "American Express", url: "/compnay_logo/americanexpress.jpg" },
                    { name: "Infosys", url: "/compnay_logo/infosys.jpg" },
                    { name: "Dell", url: "/compnay_logo/dell.png" },
                    { name: "Hyundai", url: "/compnay_logo/hyundai.jpg" },
                    { name: "IBM", url: "/compnay_logo/ibm.jpg" },
                    { name: "Accenture", url: "/compnay_logo/accenture.jpg" }
                  ].map((logo, i) => (
                    <div key={i} className="flex flex-col items-center justify-center gap-4 opacity-70 transition-all hover:opacity-100">
                      <div className="relative h-16 w-32 sm:h-20 sm:w-40 md:h-24 md:w-48 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-sm border border-border/50">
                        <Image src={logo.url} alt={logo.name} fill className="object-contain p-2" />
                      </div>
                      <span className="text-xs font-semibold text-muted-foreground tracking-wide">{logo.name}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. Why Us & Enquiry Form Section */}
        <section className="relative py-16 md:py-24 bg-background overflow-hidden border-b border-border/50 gsap-section">
          <div className="container relative z-10 mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-12 xl:gap-16 items-center justify-between w-full">
              <div className="relative w-full flex flex-col items-start text-left gsap-fade-up">
                <div className="flex flex-col gap-6 lg:gap-8 max-w-2xl">
                  <div className="space-y-4">
                    <Badge variant="outline" className="mb-2 border-mont-violet/40 text-mont-violet bg-mont-violet/5">
                      Why MONT2 Academy
                    </Badge>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-heading text-foreground">
                      Why Choose <span className="text-mont-violet">MONT2?</span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                      We blend industry-standard tools with hands-on mentoring.
                      Our curriculum is tailored for both beginners and working professionals aiming to accelerate their data careers.
                      We emphasize real-world projects over purely theoretical learning, ensuring you build portfolio-grade outputs
                      that stand out to hiring managers.
                    </p>
                    <ul className="space-y-4 text-base text-muted-foreground mt-6">
                      <li className="flex items-center gap-4">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-primary text-white font-bold text-sm shadow-md">✓</span>
                        <span className="font-semibold text-foreground">Industry-expert trainers</span>
                      </li>
                      <li className="flex items-center gap-4">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-primary text-white font-bold text-sm shadow-md">✓</span>
                        <span className="font-semibold text-foreground">100% placement assistance</span>
                      </li>
                      <li className="flex items-center gap-4">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-primary text-white font-bold text-sm shadow-md">✓</span>
                        <span className="font-semibold text-foreground">Flexible batch timings</span>
                      </li>
                    </ul>

                    <div className="mt-8 p-5 rounded-2xl border border-border/80 bg-card/40 backdrop-blur-md flex flex-col sm:flex-row items-center gap-5 relative overflow-hidden group hover:border-mont-violet/40 transition-all duration-300">
                      <div className="relative w-24 h-32 shrink-0 rounded-lg overflow-hidden border border-border shadow-md transition-transform duration-300 group-hover:scale-105">
                        <Image
                          src="/iso_certified.jpeg"
                          alt="ISO 9001:2015 Certificate"
                          fill
                          className="object-cover"
                          sizes="96px"
                        />
                      </div>
                      <div className="flex-1 text-center sm:text-left">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-xs font-bold uppercase tracking-wider mb-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          ISO 9001:2015 Certified
                        </div>
                        <h4 className="font-bold text-foreground text-lg mb-1">Quality Management System</h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          MONT2 Academy is certified by Toris Management Pvt. Ltd. for providing academic education, professional training, and skill development services.
                        </p>
                        <a 
                          href="/mont2-iso-certificate.pdf" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-bold text-mont-violet mt-3 hover:underline"
                        >
                          View Full Certificate <ExternalLink size={12} />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hero-enquiry-card relative w-full max-w-md mx-auto lg:mx-0 lg:ml-auto gsap-fade-up">
                <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-mont-violet/40 via-mont-orange/25 to-transparent blur-xl opacity-70" />
                <div className="relative rounded-3xl border border-white/15 bg-card/85 backdrop-blur-xl shadow-[0_25px_80px_rgba(0,0,0,0.15)] p-6 sm:p-8 overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-mont-violet/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
                  <div className="relative space-y-5">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <Badge variant="outline" className="mb-2 border-mont-violet/40 text-mont-violet bg-background/50">
                          Priority callback
                        </Badge>
                        <h2 className="text-xl sm:text-2xl font-bold font-heading text-foreground leading-snug">
                          Quick student enquiry
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          Share your details
                        </p>
                      </div>
                    </div>

                    <form onSubmit={handleHeroEnquiry} className="space-y-4">
                      <Input
                        name="name"
                        required
                        placeholder="Full name"
                        className="h-11 rounded-xl border-border bg-background/80"
                      />
                      <Input
                        name="phone"
                        required
                        type="tel"
                        placeholder="Phone (WhatsApp preferred)"
                        className="h-11 rounded-xl border-border bg-background/80"
                      />
                      <Input
                        name="email"
                        type="email"
                        required
                        placeholder="Email"
                        className="h-11 rounded-xl border-border bg-background/80"
                      />
                      <select
                        name="course"
                        required
                        className="h-11 w-full rounded-xl border border-border bg-background/80 px-3 text-sm text-foreground outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                        defaultValue=""
                      >
                        <option value="" disabled hidden>
                          Course interested in
                        </option>
                        {listCourseRecords().map((c) => (
                          <option key={c.id} value={c.title}>
                            {c.title}
                          </option>
                        ))}
                      </select>
                      <textarea
                        name="message"
                        rows={3}
                        placeholder="Tell us about your background or preferred batch timing..."
                        className="w-full rounded-xl border border-border bg-background/80 px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      />
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full h-12 rounded-xl bg-gradient-primary text-white border-0 hover:opacity-90 font-semibold shadow-[0_0_24px_rgba(108,92,231,0.35)] disabled:opacity-50 mt-2"
                      >
                        {isSubmitting ? "Sending..." : "Submit Enquiry"}
                      </Button>
                      <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                        By submitting, you consent to MONT2 Academy contacting you regarding programs & batches. No spam —
                        brochure-aligned INR pricing only.
                      </p>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Visual bento — program focus */}
        <section className="relative border-b border-border bg-gradient-to-b from-card/50 via-background to-background py-20 gsap-section overflow-hidden">
          <div className="pointer-events-none absolute right-0 top-1/2 h-[420px] w-[420px] -translate-y-1/2 translate-x-1/3 rounded-full bg-mont-violet/15 blur-[100px]" />
          <div className="container relative mx-auto px-6 lg:px-12">
            <div className="mx-auto mb-12 max-w-3xl text-center gsap-fade-up">
              <Badge variant="outline" className="mb-4 border-mont-orange/35 text-mont-orange">
                Visual campus
              </Badge>
              <h2 className="text-3xl font-bold font-heading text-foreground md:text-5xl">
                Programs that <span className="text-gradient">look as serious</span> as your career goals
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Peek into the kind of work you&apos;ll ship in class — dashboards, models, and analytics stories told with
                clarity.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-12 lg:grid-rows-2 lg:gap-5">
              {BENTO_HIGHLIGHTS.map((tile, idx) => (
                <Link
                  key={tile.href}
                  href={tile.href}
                  className={cn(
                    "group relative min-h-[220px] overflow-hidden rounded-3xl border border-border/80 bg-card/30 shadow-sm transition-all duration-500 hover:border-mont-violet/45 hover:shadow-[0_24px_70px_-20px_rgba(139,92,246,0.35)] md:min-h-[260px]",
                    idx === 0 && "md:col-span-2 lg:col-span-7 lg:row-span-2 lg:min-h-[380px]",
                    idx === 1 && "lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:min-h-[180px]",
                    idx === 2 && "lg:col-span-5 lg:col-start-8 lg:row-start-2 lg:min-h-[180px]",
                  )}
                >
                  <Image
                    src={tile.image}
                    alt={tile.title}
                    fill
                    sizes="(max-width:768px) 100vw, (max-width:1200px) 50vw, 60vw"
                    className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-95 dark:from-black dark:via-black/55 dark:to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-br from-mont-violet/25 via-transparent to-mont-orange/10 opacity-70 mix-blend-overlay" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur-md">
                      {idx === 0 ? <Zap className="h-3 w-3 text-mont-orange" /> : null}
                      Featured
                    </div>
                    <h3 className="text-2xl font-bold font-heading text-white drop-shadow-lg md:text-3xl">{tile.title}</h3>
                    <p className="mt-2 max-w-md text-sm font-medium text-white/85">{tile.subtitle}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white">
                      View details
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Tool Stack Fan Out Deck */}
        <section className="relative border-b border-border bg-muted/25 py-16 gsap-section">
          <div className="container mx-auto px-6 mb-12 text-center gsap-fade-up">
            <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground">
              The <span className="text-gradient">Tech Stack</span> You'll Master
            </h2>
            <p className="mt-3 text-muted-foreground text-sm md:text-base">
              Get hands-on experience with the most in-demand data tools. Tap the stack on mobile to fan out.
            </p>
          </div>

          <div className="container mx-auto max-w-6xl px-4 gsap-fade-up">
            <FanOutDeck />
          </div>
        </section>

        {/* 7. Split story blocks (Everything You Need To Crack Tech Interviews) */}
        <section className="py-20 gsap-section">
          <div className="container mx-auto px-6 mb-16 text-center gsap-fade-up">
            <h2 className="text-3xl font-bold font-heading text-foreground md:text-5xl">
              Everything You Need To <span className="text-gradient">Crack Tech Interviews</span>
            </h2>
          </div>
          <div className="container mx-auto space-y-20 px-6 lg:px-12">
            {STORY_BLOCKS.map((block) => (
              <div
                key={block.title}
                className="gsap-fade-up grid items-center gap-10 lg:grid-cols-2 lg:gap-16"
              >
                <div
                  className={cn(
                    "relative aspect-[4/3] overflow-hidden rounded-3xl border border-border shadow-[0_20px_60px_-24px_rgba(0,0,0,0.35)] lg:aspect-[5/4]",
                    !block.imageLeft && "lg:order-2",
                  )}
                >
                  <Image
                    src={block.image}
                    alt={block.title}
                    fill
                    className="object-cover"
                    sizes="(max-width:1024px) 100vw, 50vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-mont-violet/30 via-transparent to-mont-orange/20" />
                  <div className="absolute left-4 top-4 rounded-full border border-white/20 bg-black/40 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur-md">
                    MONT2 Academy
                  </div>
                </div>
                <div className={cn("space-y-5", !block.imageLeft && "lg:order-1")}>
                  <Badge variant="outline" className="border-mont-violet/40 text-mont-violet">
                    {block.eyebrow}
                  </Badge>
                  <h3 className="text-3xl font-bold font-heading leading-tight text-foreground md:text-4xl">{block.title}</h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">{block.body}</p>
                  <div className="flex flex-wrap gap-3 pt-2">
                    {block.eyebrow === "Why Mont2" ? (
                      <Link href={block.href}>
                        <Button className="rounded-xl border-0 bg-gradient-primary px-6 text-white hover:opacity-90">
                          Take a Tour of Academy
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    ) : (
                      <>
                        <Link href={block.href}>
                          <Button className="rounded-xl border-0 bg-gradient-primary px-6 text-white hover:opacity-90">
                            Explore section
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                        <Link href="/contact">
                          <Button variant="outline" className="rounded-xl border-border">
                            Talk to a counselor
                          </Button>
                        </Link>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 pt-4 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-2 rounded-xl border border-border/80 bg-card/40 px-3 py-2">
                      <BrainCircuit className="h-4 w-4 text-mont-violet" />
                      Live-style mentoring
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-xl border border-border/80 bg-card/40 px-3 py-2">
                      <BarChart3 className="h-4 w-4 text-mont-orange" />
                      Portfolio-grade outputs
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 8. Success Stories Infinite Rail */}
        <section className="relative overflow-hidden py-16 bg-background border-t border-border gsap-section">
          <div className="container mx-auto px-6 lg:px-12 mb-10 text-center gsap-fade-up">
            <Badge variant="outline" className="mb-4 border-mont-violet/40 text-mont-violet">
              Success Stories
            </Badge>
            <h2 className="text-3xl font-bold font-heading text-foreground md:text-4xl">
              Hear from our <span className="text-gradient">Alumni</span>
            </h2>
            <p className="mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">
              Real stories from real learners who accelerated their data careers with us.
            </p>
          </div>

          <div className="relative flex max-w-full overflow-hidden bg-background py-4">
            {/* Left/Right fading gradients for smooth entering/exiting */}
            <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-24 bg-gradient-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-24 bg-gradient-to-l from-background to-transparent" />

            <div className="flex w-max animate-marquee-slow items-center gap-6 px-4 hover:[animation-play-state:paused]">
              {marqueeStories.map((story, i) => (
                <div
                  key={`${story.name}-${i}`}
                  className="flex h-full w-[280px] flex-col rounded-2xl border border-border bg-card/60 p-5 shadow-sm backdrop-blur-sm transition-all hover:border-mont-violet/40 hover:shadow-md sm:w-[320px] md:w-[380px] md:p-6"
                >
                  <p className="flex-1 text-muted-foreground italic mb-6">
                    &quot;{story.quote}&quot;
                  </p>
                  <div className="flex items-center gap-4 mt-auto border-t border-border/50 pt-4">
                    <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-full border-2 border-border">
                      <Image
                        src={story.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(story.name)}&background=6c5ce7&color=fff`}
                        alt={story.name}
                        fill
                        className="object-cover"
                        sizes="48px"
                      />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground text-sm font-heading">{story.name}</h4>
                      <p className="text-xs text-muted-foreground font-medium mt-0.5">{story.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 9. Bottom CTA */}
        <section className="relative py-20 gsap-section">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="gsap-fade-up relative overflow-hidden rounded-3xl border border-mont-violet/30 bg-gradient-to-br from-mont-violet/25 via-card to-mont-orange/15 p-10 md:p-14 text-center shadow-[0_0_60px_-12px_rgba(139,92,246,0.45)]">
              <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-mont-orange/30 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-mont-violet/35 blur-3xl" />
              <div className="relative mx-auto max-w-2xl space-y-6">
                <h2 className="text-3xl font-bold font-heading text-foreground md:text-4xl">
                  Ready to map your <span className="text-gradient">next career move?</span>
                </h2>
                <p className="text-muted-foreground text-lg">
                  Browse INR-priced courses, check the upcoming batch page, or message us on WhatsApp — same counseling team
                  across every route.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Link href="/courses">
                    <Button className="h-12 rounded-xl bg-foreground px-8 text-background hover:bg-foreground/90">
                      View all courses
                    </Button>
                  </Link>
                  <Link href={SITE_WHATSAPP_URL_DEFAULT} target="_blank" rel="noopener noreferrer">
                    <Button
                      variant="outline"
                      className="h-12 rounded-xl border-border bg-background/60 backdrop-blur-sm"
                    >
                      <MessageCircle className="mr-2 h-4 w-4 text-[#22c55e]" />
                      WhatsApp us
                    </Button>
                  </Link>
                  <Link href={`tel:${SITE_PHONE_E164}`}>
                    <Button variant="ghost" className="h-12 rounded-xl text-foreground">
                      <Phone className="mr-2 h-4 w-4" />
                      Call now
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
